/**
 * Tiny, dependency-free i18n layer (docs/V2_I18N_PLAN.md). `t(key)` / `t(key, {n})` interpolation +
 * `setLocale()` / `getLocale()` — no framework; plurals via `tPlural` over the native `Intl.PluralRules`. `i18n/locales/en.ts` is the source
 * of truth; every other locale is `Record<TranslationKey, string>` (see `TranslationKey` below), so
 * `tsc` fails on a missing/extra key — the compiler is the completeness test, not a runtime fallback.
 *
 * This extraction pass ships ONLY `en` (docs/V2_I18N_PLAN.md's process: extraction first, alone,
 * verified string-identical; translation is ux-copywriter's later pass). Adding a locale is meant to
 * be exactly two steps: write `locales/<code>.ts`, register it in `REGISTERED_LOCALES` below — the
 * side-nav switcher's option list is BUILT from that record (`listLocales()`), so nothing else needs
 * to change to surface a new language.
 *
 * PHASE 2 (docs/V2_I18N_PLAN.md §4): a logged-in user's account carries its own `locale`
 * (`users.locale`, exposed on the login/register response and persisted from here — see
 * `setLocale()` below). It is preferred over this browser's own `localStorage` at boot, but an
 * ANONYMOUS visitor's resolution chain is `localStorage` -> `"en"` (the browser locale was dropped
 * from it on 2026-08-01 — see `resolveInitialLocale()`). The public rider viewer's `?lang=` uses a
 * separate, non-persisting `applyLocaleOverride()` instead of any of this (see that function's doc
 * comment) — a shared link must not touch the visitor's own stored preference.
 */
import { en } from "./locales/en";
import { getStoredUser, updateStoredUserLocale } from "../api/http";
import { updateAccountLocale } from "../api/account-api";
import { cachedUiOverrides, setUiOverrides, uiOverride } from "./overrides";

export type TranslationKey = keyof typeof en;
/** Полный словарь одного языка — тот же набор ключей, что у `en` (его проверяет `tsc`). */
export type Dictionary = Record<TranslationKey, string>;
/** Widen this union as translated dictionaries land, e.g. `"en" | "ru" | "es" | "fr" | "zh" | "de"`. */
export type Locale = "en" | "ru" | "es" | "fr" | "de" | "zh";

/** `localStorage` key for the persisted choice — analogous to `components/theme.ts`'s own
 *  `scb-theme` key (same persistence pattern, a different concern). */
const STORAGE_KEY = "scb-locale";

/** Every locale dictionary that actually exists, keyed by locale code, with the human-readable label
 *  the side-nav `<select>` shows for it (docs/V2_I18N_PLAN.md: "flag-free, text labels: EN РУ ES FR
 *  中文 DE"). Add an entry here (and its `locales/<code>.ts`) and the whole app picks it up. */
const REGISTERED_LOCALES: Record<Locale, { label: string; load: () => Promise<Dictionary> }> = {
  en: { label: "ENG", load: async () => en },
  ru: { label: "РУС", load: async () => (await import("./locales/ru")).ru },
  es: { label: "ESP", load: async () => (await import("./locales/es")).es },
  fr: { label: "FRA", load: async () => (await import("./locales/fr")).fr },
  de: { label: "DEU", load: async () => (await import("./locales/de")).de },
  zh: { label: "中文", load: async () => (await import("./locales/zh")).zh },
};

/**
 * ОДИН СЛОВАРЬ НА ЭКРАНЕ, А НЕ ШЕСТЬ В КАЖДОЙ СТРАНИЦЕ (SCRUM-887).
 *
 * Владелец 28.10.2026: «и оно всё хуже и тормознее работает», «весь сайт». Замер того же дня: 81%
 * главного файла скриптов — это шесть словарей сразу (в исходниках 992 КБ: ru 211, fr 167, de 165,
 * es 161, zh 151, en 150), хотя человек видит один. `en` остаётся статическим, потому что он ещё и
 * запасной путь для ключа, которого в словаре языка не оказалось; остальные пять — отдельные файлы,
 * которые приезжают по требованию.
 *
 * ПОЧЕМУ ЭТО БЕЗОПАСНО ДЛЯ СИНХРОННОГО `t()`. Переключение языка перезагружает страницу
 * (`setLocale` ниже — осознанный размен из `docs/V2_I18N_PLAN.md`), поэтому в пределах одной
 * загрузки активный язык ОДИН и известен сразу. Его словарь ждут до первой отрисовки (`main.ts`),
 * и `t()` остаётся синхронным. Если словарь почему-то не приехал (нет сети, упал файл), строка
 * остаётся английской — на экране английский текст, а не ключи и не пустоты.
 */
const loaded: Partial<Record<Locale, Dictionary>> = { en };

/** Лежит ли словарь этого языка в памяти. Нужен тому, кто рисует НЕ активный язык и не может
 *  ждать (`pages/admin-page.ts`): `dictionaryFor` без загрузки бросает, и спросить заранее — честнее,
 *  чем ловить исключение. */
export function dictionaryLoaded(locale: Locale): boolean {
  return loaded[locale] !== undefined;
}

/** Словарь языка: из памяти, если уже приезжал, иначе загрузить и запомнить. Единственный путь
 *  получить словарь НЕ активного языка (его просит редактор формулировок у администратора). */
export async function loadDictionary(locale: Locale): Promise<Dictionary> {
  const have = loaded[locale];
  if (have) return have;
  const dict = await REGISTERED_LOCALES[locale].load();
  loaded[locale] = dict;
  return dict;
}

/** Ждёт словарь АКТИВНОГО языка — вызывается один раз до первой отрисовки (`main.ts`). Для
 *  английского это уже готовое значение и ни одного запроса. */
export function loadActiveDictionary(): Promise<Dictionary> {
  return loadDictionary(currentLocale);
}

function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(REGISTERED_LOCALES, value);
}

/**
 * A logged-in user's ACCOUNT locale -> the persisted choice -> `"en"`. Guarded for non-browser/test
 * environments (vitest's node test environment has no `document`/`navigator`/`localStorage`) so
 * importing this module never throws outside a real page.
 *
 * The account-locale check is first (docs/V2_I18N_PLAN.md phase 2, "prefer it over localStorage at
 * boot for a logged-in user") — `getStoredUser()` reads the SAME `localStorage` entry
 * `auth-service.ts` restores its session from at module load, so this stays synchronous (no extra
 * network round trip) and works the instant a fresh device logs in, even before that device has its
 * own `scb-locale` entry. An anonymous visitor has no stored user, so this branch never fires for
 * them — their resolution chain is exactly what it was before phase 2.
 */
function resolveInitialLocale(): Locale {
  if (typeof localStorage !== "undefined") {
    try {
      const user = getStoredUser();
      if (user && isLocale(user.locale)) return user.locale;
    } catch {
      /* corrupt/unavailable stored session — fall through. */
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isLocale(stored)) return stored;
    } catch {
      /* localStorage unavailable — fall through to the browser/default. */
    }
  }
  // English, NOT `navigator.language` (owner decision 2026-08-01). The browser locale used to sit
  // here as a third step; it guessed wrong often enough to be worse than a predictable default —
  // this is an international product whose users routinely run an OS in one language and work in
  // another, and a visitor who lands in a language they didn't pick has no way to know the choice
  // was made for them. Both explicit signals above still win: an account locale, and a stored
  // choice from the language switcher.
  return "en";
}

let currentLocale: Locale = resolveInitialLocale();

/** Keeps `<html lang>` in sync (a11y + correct CJK font selection, docs/V2_I18N_PLAN.md). No-op
 *  outside a browser (no `document`). */
function syncHtmlLang(locale: Locale): void {
  if (typeof document !== "undefined") document.documentElement.setAttribute("lang", locale);
}
syncHtmlLang(currentLocale);

export function getLocale(): Locale {
  return currentLocale;
}

/**
 * Persists the choice and reloads — a full `location.reload()` is an ACCEPTED v1 tradeoff for this
 * feature (docs/V2_I18N_PLAN.md): every string on screen is guaranteed fresh, no live-re-render
 * bookkeeping needed; live re-render is a later refinement.
 *
 * PHASE 2: for a LOGGED-IN user, this also persists the choice to the account (via
 * `account-api.ts#updateAccountLocale`) so it follows them to a new device/browser, not just this
 * one — best-effort (the reload still happens even if the write fails or is slow; this device's own
 * `localStorage` entry above is already authoritative for what THIS browser shows next). An
 * anonymous visitor has nothing to persist server-side and reloads immediately, same as before.
 */
export function setLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* localStorage unavailable — the choice still applies for this session via reload below. */
  }
  syncHtmlLang(locale);
  currentLocale = locale;

  const user = (() => {
    try {
      return getStoredUser();
    } catch {
      return null;
    }
  })();
  if (user) {
    // Must happen BEFORE the reload and independently of the request below: `resolveInitialLocale()`
    // reads the stored user ahead of `scb-locale`, so leaving the login-time value in place made the
    // page come back in the previous language and the switcher appear broken (SCRUM-127).
    try {
      updateStoredUserLocale(locale);
    } catch {
      /* storage unavailable — `scb-locale` above still applies for this browser. */
    }
    void updateAccountLocale(locale)
      .catch(() => undefined)
      .finally(() => location.reload());
  } else {
    location.reload();
  }
}

/**
 * Applies `locale` for the CURRENT page load only — no `localStorage` write, no reload, no server
 * persistence. Used by the public rider viewer's `?lang=` query param (docs/V2_I18N_PLAN.md phase
 * 2): a shared link should open in the language the sender chose, without touching whatever the
 * visitor opening it has stored for themselves elsewhere in the app. Falls back to whatever
 * `resolveInitialLocale()` already picked (unchanged) when `locale` isn't a recognised code — same
 * "absent or unrecognized -> normal resolution order" behaviour the caller is asked to preserve.
 * Returns the locale that was active before the call, so a caller (e.g. on `dispose()`) can restore
 * it if the SPA keeps running after this page unmounts.
 */
export function applyLocaleOverride(locale: string | null | undefined): Locale {
  const previous = currentLocale;
  if (isLocale(locale)) {
    currentLocale = locale;
    syncHtmlLang(locale);
  }
  return previous;
}

/** Код языка из параметра ссылки, или `null`, если это не наш язык. Нужен вызывающему, который
 *  должен ЗАГРУЗИТЬ словарь до отрисовки, — а решение «применять ли» остаётся за
 *  `applyLocaleOverride` (SCRUM-887). */
export function localeFromParam(value: string | null | undefined): Locale | null {
  return isLocale(value) ? value : null;
}

/** `[code, label]` pairs for every registered locale, in declaration order — what the side-nav
 *  switcher renders as `<option>`s. */
export function listLocales(): Array<[Locale, string]> {
  return (Object.keys(REGISTERED_LOCALES) as Locale[]).map((code) => [code, REGISTERED_LOCALES[code].label]);
}

/** Translates `key`, interpolating `{name}`-style placeholders from `vars` (e.g. `t("a.b", { n: 3 })`
 *  replaces every `{n}` in the string). No plural library (docs/V2_I18N_PLAN.md): for a counted noun use
 *  `tPlural` below (native `Intl.PluralRules`); older callers pick between per-form keys themselves (see
 *  e.g. `editor/power.ts`). Falls back to English, then the raw key, if a dictionary is ever missing an
 *  entry at runtime — `tsc` is what actually enforces completeness (see module doc); this is defense
 *  in depth only. */
/**
 * The baked dictionary for a locale — what the app ships with, before any admin override.
 *
 * Exported for the admin editor (SCRUM-43), which needs BOTH the key list and each key's shipped
 * text to show as the placeholder. The API has no copy of either: keeping the dictionary on one
 * side of the boundary is what stops it from rotting on the other.
 *
 * БРОСАЕТ, А НЕ ОТДАЁТ АНГЛИЙСКИЙ, когда словарь ещё не загружен (SCRUM-887). Запасной путь был бы
 * здесь хуже отказа: проверки полноты переводов (`rental/answer-coverage.test.ts` и соседи) просят
 * словарь КАЖДОГО языка и утверждают, что ключ в нём есть, — с подстановкой `en` они бы шесть раз
 * проверили английский и прошли, ничего не проверив. Зови `loadDictionary(locale)` перед этим.
 */
export function dictionaryFor(locale: Locale): Record<string, string> {
  const dict = loaded[locale];
  if (!dict) {
    throw new Error(`i18n: словарь «${locale}» ещё не загружен — сперва await loadDictionary("${locale}")`);
  }
  return dict;
}

export function t(key: TranslationKey, vars?: Record<string, string | number>): string {
  const dict = loaded[currentLocale] ?? en;
  // Admin override -> this locale's baked dictionary -> English -> the key itself (SCRUM-43). The
  // override is per locale, so rewording one language leaves the other five on the shipped copy.
  let str: string = uiOverride(key) ?? dict[key] ?? en[key] ?? key;
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      str = str.replaceAll(`{${name}}`, String(value));
    }
  }
  return str;
}

/**
 * Loads the admin's interface-string overrides for the locale in force (SCRUM-43).
 *
 * Call ONCE before the app shell mounts. It resolves the cached copy immediately and refreshes it
 * from the API in the background, so boot is never blocked on a network round trip: a slow or dead
 * API must cost nothing more than seeing the shipped wording, which is the correct fallback.
 */
export function initUiOverrides(fetchOverrides: (locale: string) => Promise<Record<string, string>>): void {
  const locale = getLocale();
  const cached = cachedUiOverrides(locale);
  if (Object.keys(cached).length > 0) setUiOverrides(locale, cached);
  void fetchOverrides(locale)
    .then((fresh) => setUiOverrides(locale, fresh))
    .catch(() => undefined);
}

/**
 * A counted noun: `tPlural("gearList.mixing.inputs", 3)` reads `gearList.mixing.inputs.<category>` and
 * replaces `{n}` with the count (SCRUM-522). The category is the platform's own CLDR rule for the
 * current locale (`Intl.PluralRules`) — `one`/`other` in English, `one`/`few`/`many` in Russian — so no
 * rule table lives here. A locale writes the form it needs under each of the four categories this
 * product uses; a category it leaves identical to `other` is simply the same text. Counts here are
 * whole numbers (list lengths).
 */
export function tPlural(base: string, count: number): string {
  const category = new Intl.PluralRules(currentLocale).select(count);
  const form = (["one", "few", "many"] as const).includes(category as "one") ? category : "other";
  return t(`${base}.${form}` as TranslationKey, { n: count });
}
